import { Box, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { HeapActivityActions } from './HeapActivityActions';
import { HeapDetectionTable } from './HeapDetectionTable';
import { HeapImageWithBoxes } from './HeapImageWithBoxes';
import { DetectionRowView, HeapActivityHeaderItem, HeapDetection } from './types';
import { useTranslation } from 'react-i18next';

interface HeapActivityCardProps {
  imageUrl: string;
  detections: HeapDetection[];
  headerItems: HeapActivityHeaderItem[];
  detectionRows: DetectionRowView[];
  emptyMessage: string;
  classLabel: string;
  countLabel: string;
  modelClassLabel: string;
  invalidDetectionIndexes?: number[];
  onInvalidateDetection?: (detectionIndex: number) => void;
  onUndoInvalidation?: () => void;
  onSubmitComplaint: (text: string) => Promise<void>;
  isSubmitComplaintPending?: boolean;
}

export function HeapActivityCard({
  imageUrl,
  detections,
  headerItems,
  detectionRows,
  emptyMessage,
  classLabel,
  countLabel,
  invalidDetectionIndexes,
  onInvalidateDetection,
  onUndoInvalidation,
  modelClassLabel,
  onSubmitComplaint,
  isSubmitComplaintPending,
}: HeapActivityCardProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Box display='flex' flexDirection='column' height='100%'>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: 0.5,
          pr: 2,
          pl: 2,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {headerItems.map((item, index) => (
          <Box key={index} display='flex' alignItems='center' justifyContent='center' gap={2} p={1}>
            {item.icon}
            {item.label && (
              <Typography
                variant='body2'
                textAlign='center'
                fontWeight={500}
                color='text.secondary'
                sx={{ mt: '2px', ml: -1.5 }}
              >
                {t(item.label)}
              </Typography>
            )}
            <Typography variant='body1' noWrap>
              {t(...item.value)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          padding: 2,
          boxSizing: 'border-box',
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          gap: 2,
        }}
      >
        <Box sx={{ flex: 2, minHeight: { xs: 'auto', lg: 0 }, display: 'flex', overflow: 'hidden', borderRadius: 2 }}>
          <HeapImageWithBoxes
            imageSrc={imageUrl}
            detections={detections}
            invalidDetectionIndexes={invalidDetectionIndexes}
            onDetectionClick={onInvalidateDetection}
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, gap: 1 }}>
          <HeapActivityActions
            onUndoInvalidation={onUndoInvalidation}
            invalidDetectionIndexes={invalidDetectionIndexes}
            onSubmitComplaint={onSubmitComplaint}
            isSubmitComplaintPending={isSubmitComplaintPending}
          />
          <Box sx={{ flex: 1, minHeight: 0, backgroundColor: '#eeeeee', borderRadius: 2 }}>
            <HeapDetectionTable
              rows={detectionRows}
              emptyMessage={emptyMessage}
              classLabel={classLabel}
              countLabel={countLabel}
              modelClassLabel={modelClassLabel}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
